import { useQueryClient } from '@tanstack/react-query';
import { userToMember } from '../../members/utils/userToMember.js';
import { memberPermissionsQueryPredicates } from './memberPermissionsQueryPredicates.js';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { useOptimisticallyUpdateQueriesWithPredicate } from '@fabric-app/queries/utilities';

const useQueryCachePermissionMemberHelpers = () => {
  const { data: user } = useQueryUserInfo();
  const queryClient = useQueryClient();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  return {
    addSelfToCachedSpace: (spaceId, role) => {
      if (!user) return;
      queryClient.cancelQueries({
        predicate: (q) => memberPermissionsQueryPredicates.byResourceId(q, spaceId)
      });
      const optimisticallyUpdateSpaceMembersQueries = optimisticallyUpdateQueries((q) => memberPermissionsQueryPredicates.byResourceId(q, spaceId), (data) => [
        userToMember(user, role),
        ...data ?? []
      ]);
      return {
        optimisticallyUpdateSpaceMembersQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateSpaceMembersQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateSpaceMembersQueries.invalidateQueries();
        }
      };
    },
    updatePermissionMemberRole: (variables) => {
      queryClient.cancelQueries({
        predicate: (q) => memberPermissionsQueryPredicates.byResourceId(q, variables.resourceId)
      });
      const optimisticallyUpdatePermissionMembersQueries = optimisticallyUpdateQueries((q) => memberPermissionsQueryPredicates.byResourceId(q, variables.resourceId), (data) => data ? data.map((permission) => {
        if (permission.user.id === variables.userId) {
          return {
            ...permission,
            role: variables.role
          };
        }
        return permission;
      }) : data);
      return {
        optimisticallyUpdatePermissionMembersQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdatePermissionMembersQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdatePermissionMembersQueries.invalidateQueries();
        }
      };
    },
    deletePermissionMemberRole: (variables) => {
      queryClient.cancelQueries({
        predicate: (q) => memberPermissionsQueryPredicates.byResourceId(q, variables.resourceId)
      });
      const optimisticallyUpdatePermissionMembersQueries = optimisticallyUpdateQueries((q) => memberPermissionsQueryPredicates.byResourceId(q, variables.resourceId), (data) => data ? data.filter((permission) => permission.user.id !== variables.userId) : data);
      return {
        optimisticallyUpdatePermissionMembersQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdatePermissionMembersQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdatePermissionMembersQueries.invalidateQueries();
        }
      };
    },
    removeSelfFromCachedSpace: (spaceId) => {
      if (!user) return;
      queryClient.cancelQueries({
        predicate: (q) => memberPermissionsQueryPredicates.byResourceId(q, spaceId)
      });
      const optimisticallyUpdateSpaceMembersQueries = optimisticallyUpdateQueries((q) => memberPermissionsQueryPredicates.byResourceId(q, spaceId), (data) => data?.filter((member) => member.id !== user.id) ?? []);
      return {
        optimisticallyUpdateSpaceMembersQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateSpaceMembersQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateSpaceMembersQueries.invalidateQueries();
        }
      };
    }
  };
};

export { useQueryCachePermissionMemberHelpers };
//# sourceMappingURL=useQueryCachePermissionMembersHelpers.js.map
